import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

// ─── Constants ────────────────────────────────────────────────────────────────

const FREQ_OPTIONS = [
  { value: 'immediate', label: 'Immédiat', description: 'En temps réel', icon: 'Zap' },
  { value: 'daily', label: 'Quotidien', description: 'Résumé du matin', icon: 'Sun' },
  { value: 'weekly', label: 'Hebdomadaire', description: 'Chaque lundi', icon: 'CalendarDays' },
  { value: 'never', label: 'Jamais', description: 'Désactivé', icon: 'BellOff' },
];

const EMAIL_CATEGORIES = [
  {
    key: 'notifications',
    label: 'Notifications de compte',
    description: 'Rendez-vous, demandes de propriété, messages',
    icon: 'Bell',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    accentColor: 'blue',
    defaultFreq: 'immediate',
    canDisable: false,
    tip: 'Ces emails sont essentiels pour votre compte.',
  },
  {
    key: 'marketing',
    label: 'Emails marketing',
    description: 'Nouvelles propriétés, offres spéciales, tendances du marché',
    icon: 'TrendingUp',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    accentColor: 'amber',
    defaultFreq: 'weekly',
    canDisable: true,
    tip: 'Recevez moins souvent pour réduire la fatigue email.',
  },
  {
    key: 'legal',
    label: 'Mises à jour légales',
    description: "Conditions d'utilisation, politique de confidentialité, conformité",
    icon: 'Scale',
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-600',
    accentColor: 'purple',
    defaultFreq: 'immediate',
    canDisable: false,
    tip: 'Requis par la loi. Fréquence limitée.',
  },
];

const DIGEST_OPTIONS = [
  {
    key: 'digest_enabled',
    label: 'Activer le digest hebdomadaire',
    description: 'Regroupez vos notifications en un seul email récapitulatif par semaine',
    icon: 'Layers',
    iconBg: 'bg-teal-50',
    iconColor: 'text-teal-600',
    badge: 'Recommandé',
    badgeColor: 'bg-teal-100 text-teal-700',
  },
  {
    key: 'smart_batching',
    label: 'Regroupement intelligent',
    description: 'Regroupe les notifications similaires pour éviter les doublons',
    icon: 'Sparkles',
    iconBg: 'bg-indigo-50',
    iconColor: 'text-indigo-600',
    badge: null,
  },
  {
    key: 'unsubscribe_protection',
    label: 'Protection anti-désabonnement',
    description: 'Réduit automatiquement la fréquence si vous ignorez nos emails',
    icon: 'ShieldCheck',
    iconBg: 'bg-green-50',
    iconColor: 'text-green-600',
    badge: 'Anti-spam',
    badgeColor: 'bg-green-100 text-green-700',
  },
];

const DEFAULT_PREFS = {
  notifications_freq: 'immediate',
  notifications_email: true,
  notifications_sms: true,
  marketing_freq: 'weekly',
  marketing_email: true,
  marketing_sms: false,
  legal_freq: 'immediate',
  legal_email: true,
  legal_sms: false,
  digest_enabled: true,
  smart_batching: true,
  unsubscribe_protection: true,
  digest_day: 1,
  digest_hour: 8,
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const Toggle = ({ checked, onChange, disabled = false, color = 'brand-earth' }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
      checked ? 'bg-brand-earth' : 'bg-gray-200'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

const FreqPill = ({ value, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-ios-caption1 font-body-medium border transition-all duration-150 ${
      selected
        ? 'bg-brand-earth text-white border-brand-earth shadow-ios-light'
        : 'bg-white text-text-secondary border-border/60 hover:border-brand-earth/40 hover:text-brand-earth'
    }`}
  >
    <Icon name={value?.icon} size={12} className={selected ? 'text-white' : ''} />
    <span>{value?.label}</span>
  </button>
);

const SectionCard = ({ icon, iconBg, iconColor, title, subtitle, children, badge = null }) => (
  <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
      <div className="flex items-center space-x-3">
        <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon name={icon} size={18} className={iconColor} />
        </div>
        <div>
          <p className="text-ios-callout font-body-semibold text-text-primary">{title}</p>
          {subtitle && <p className="text-ios-caption1 text-text-secondary mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {badge && (
        <span className={`text-ios-caption2 font-body-medium px-2.5 py-1 rounded-full ${badge?.color}`}>
          {badge?.label}
        </span>
      )}
    </div>
    <div className="px-5 py-5">{children}</div>
  </div>
);

const SaveButton = ({ saving, saved, onClick }) => (
  <div className="flex items-center justify-end space-x-3 pt-3 mt-1 border-t border-border/30">
    {saved && (
      <span className="flex items-center space-x-1.5 text-ios-caption1 text-green-600 bg-green-50 px-3 py-1.5 rounded-xl border border-green-100">
        <Icon name="CheckCircle" size={13} />
        <span>Paramètres sauvegardés</span>
      </span>
    )}
    <button
      onClick={onClick}
      disabled={saving}
      className="flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 disabled:opacity-60 transition-all duration-200 shadow-ios-light"
    >
      {saving ? (
        <>
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span>Sauvegarde…</span>
        </>
      ) : (
        <>
          <Icon name="Save" size={15} color="white" />
          <span>Sauvegarder</span>
        </>
      )}
    </button>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const UserEmailSettings = () => {
  const { user } = useAuth();

  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const loadPrefs = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error: fetchErr } = await supabase
        ?.from('user_profiles')
        ?.select('notification_preferences')
        ?.eq('id', user?.id)
        ?.maybeSingle();

      if (!fetchErr && data?.notification_preferences) {
        setPrefs(prev => ({ ...prev, ...data?.notification_preferences }));
      }
    } catch (err) {
      console.error('Error loading email settings:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    setError('');
    try {
      const { error: saveErr } = await supabase
        ?.from('user_profiles')
        ?.update({ notification_preferences: prefs, updated_at: new Date()?.toISOString() })
        ?.eq('id', user?.id);

      if (saveErr) {
        setError('Erreur lors de la sauvegarde. Veuillez réessayer.');
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 3500);
      }
    } catch (err) {
      setError('Erreur inattendue. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  const setFreq = (category, value) => setPrefs(p => ({ ...p, [`${category}_freq`]: value }));
  const setChannel = (category, channel, value) => setPrefs(p => ({ ...p, [`${category}_${channel}`]: value }));
  const setDigest = (key, value) => setPrefs(p => ({ ...p, [key]: value }));

  if (!user) {
    return (
      <div className="min-h-screen bg-surface/30">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-brand-earth/10 flex items-center justify-center mx-auto mb-5">
            <Icon name="Lock" size={28} className="text-brand-earth" />
          </div>
          <h2 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-3">
            Connexion requise
          </h2>
          <p className="text-ios-callout text-text-secondary mb-6">
            Connectez-vous pour gérer vos paramètres d'emails et notifications.
          </p>
          <Link
            to="/auth"
            className="inline-flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 transition-colors"
          >
            <Icon name="LogIn" size={16} color="white" />
            <span>Se connecter</span>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Paramètres Email & Notifications – Kapital States</title>
        <meta name="description" content="Contrôlez la fréquence de vos emails, les canaux de notification et les options de digest pour une expérience personnalisée." />
      </Helmet>

      <div className="min-h-screen bg-surface/30">
        <Header />

        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

          {/* Breadcrumb + Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-2 text-ios-caption1 text-text-secondary mb-3">
              <Link to="/homepage" className="hover:text-brand-earth transition-colors">Accueil</Link>
              <Icon name="ChevronRight" size={14} />
              <Link to="/account-dashboard" className="hover:text-brand-earth transition-colors">Mon compte</Link>
              <Icon name="ChevronRight" size={14} />
              <span className="text-text-primary">Paramètres email</span>
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-ios-title1 font-display font-display-semibold text-brand-earth leading-tight">
                  Paramètres Email & Notifications
                </h1>
                <p className="text-ios-callout text-text-secondary mt-1">
                  Contrôlez la fréquence, les canaux et les options de digest pour réduire les désabonnements
                </p>
              </div>
              <Link
                to="/notification-preferences"
                className="flex items-center space-x-1.5 px-3 py-2 rounded-xl text-ios-caption1 text-text-secondary border border-border hover:border-brand-earth/40 hover:text-brand-earth transition-all duration-150 flex-shrink-0"
              >
                <Icon name="SlidersHorizontal" size={14} />
                <span>Préférences SMS</span>
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
                <span className="text-ios-callout text-text-secondary">Chargement de vos paramètres…</span>
              </div>
            </div>
          ) : (
            <div className="space-y-6">

              {/* ── Unsubscribe risk banner ── */}
              <div className="flex items-start space-x-3 px-4 py-3.5 bg-amber-50 border border-amber-200 rounded-2xl">
                <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name="AlertTriangle" size={16} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-ios-callout font-body-semibold text-amber-800">Réduisez les désabonnements</p>
                  <p className="text-ios-caption1 text-amber-700 mt-0.5 leading-relaxed">
                    Personnalisez la fréquence et activez le digest pour recevoir moins d'emails tout en restant informé. Une fréquence adaptée réduit les désabonnements de 40%.
                  </p>
                </div>
              </div>

              {/* ── 1. Email Frequency per Category ── */}
              <SectionCard
                icon="Mail"
                iconBg="bg-brand-earth/10"
                iconColor="text-brand-earth"
                title="Fréquence des emails par catégorie"
                subtitle="Choisissez quand recevoir chaque type d'email"
              >
                <div className="space-y-6">
                  {EMAIL_CATEGORIES?.map((cat, idx) => (
                    <div key={cat?.key}>
                      {idx > 0 && <div className="border-t border-border/30 mb-6" />}
                      <div className="flex items-start space-x-3 mb-3">
                        <div className={`w-8 h-8 rounded-xl ${cat?.iconBg} flex items-center justify-center flex-shrink-0`}>
                          <Icon name={cat?.icon} size={15} className={cat?.iconColor} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-ios-callout font-body-semibold text-text-primary">{cat?.label}</p>
                          <p className="text-ios-caption1 text-text-secondary mt-0.5">{cat?.description}</p>
                        </div>
                      </div>

                      {/* Frequency pills */}
                      <div className="flex flex-wrap gap-2 mb-3 pl-11">
                        {FREQ_OPTIONS?.filter(f => cat?.canDisable ? true : f?.value !== 'never')?.map(opt => (
                          <FreqPill
                            key={opt?.value}
                            value={opt}
                            selected={prefs?.[`${cat?.key}_freq`] === opt?.value}
                            onClick={() => setFreq(cat?.key, opt?.value)}
                          />
                        ))}
                      </div>

                      {/* Tip */}
                      {cat?.tip && (
                        <div className="pl-11">
                          <p className="text-ios-caption2 text-text-secondary italic flex items-center space-x-1">
                            <Icon name="Info" size={11} className="flex-shrink-0" />
                            <span>{cat?.tip}</span>
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* ── 2. Notification Channels ── */}
              <SectionCard
                icon="Radio"
                iconBg="bg-indigo-50"
                iconColor="text-indigo-600"
                title="Canaux de notification"
                subtitle="Choisissez comment recevoir chaque catégorie"
              >
                <div className="space-y-1">
                  {/* Header row */}
                  <div className="grid grid-cols-[1fr_auto_auto] gap-4 pb-2 border-b border-border/30 mb-3">
                    <span className="text-ios-caption1 font-body-medium text-text-secondary">Catégorie</span>
                    <div className="flex items-center space-x-1 w-16 justify-center">
                      <Icon name="Mail" size={13} className="text-blue-500" />
                      <span className="text-ios-caption1 font-body-medium text-text-secondary">Email</span>
                    </div>
                    <div className="flex items-center space-x-1 w-16 justify-center">
                      <Icon name="MessageSquare" size={13} className="text-green-500" />
                      <span className="text-ios-caption1 font-body-medium text-text-secondary">SMS</span>
                    </div>
                  </div>

                  {EMAIL_CATEGORIES?.map((cat, idx) => (
                    <div
                      key={cat?.key}
                      className={`grid grid-cols-[1fr_auto_auto] gap-4 items-center py-3.5 ${idx < EMAIL_CATEGORIES?.length - 1 ? 'border-b border-border/20' : ''}`}
                    >
                      <div className="flex items-center space-x-2.5">
                        <div className={`w-7 h-7 rounded-lg ${cat?.iconBg} flex items-center justify-center flex-shrink-0`}>
                          <Icon name={cat?.icon} size={13} className={cat?.iconColor} />
                        </div>
                        <div>
                          <p className="text-ios-callout font-body-medium text-text-primary">{cat?.label}</p>
                          {prefs?.[`${cat?.key}_freq`] === 'never' && (
                            <p className="text-ios-caption2 text-red-500 flex items-center space-x-0.5 mt-0.5">
                              <Icon name="BellOff" size={10} />
                              <span>Désactivé</span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="w-16 flex justify-center">
                        <Toggle
                          checked={prefs?.[`${cat?.key}_email`] ?? true}
                          onChange={v => setChannel(cat?.key, 'email', v)}
                          disabled={!cat?.canDisable && cat?.key === 'legal'}
                        />
                      </div>
                      <div className="w-16 flex justify-center">
                        <Toggle
                          checked={prefs?.[`${cat?.key}_sms`] ?? false}
                          onChange={v => setChannel(cat?.key, 'sms', v)}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-border/30">
                  <p className="text-ios-caption2 text-text-secondary flex items-start space-x-1.5">
                    <Icon name="Info" size={12} className="flex-shrink-0 mt-0.5" />
                    <span>Les SMS nécessitent un numéro de téléphone valide dans vos <Link to="/notification-preferences" className="text-brand-earth underline underline-offset-2">préférences SMS</Link>.</span>
                  </p>
                </div>
              </SectionCard>

              {/* ── 3. Digest Options ── */}
              <SectionCard
                icon="Layers"
                iconBg="bg-teal-50"
                iconColor="text-teal-600"
                title="Options de digest"
                subtitle="Regroupez vos emails pour réduire les désabonnements"
                badge={{ label: 'Anti-désabonnement', color: 'bg-teal-100 text-teal-700' }}
              >
                <div className="space-y-4">
                  {DIGEST_OPTIONS?.map((opt, idx) => (
                    <div
                      key={opt?.key}
                      className={`flex items-start justify-between gap-4 py-4 ${idx < DIGEST_OPTIONS?.length - 1 ? 'border-b border-border/20' : ''}`}
                    >
                      <div className="flex items-start space-x-3 flex-1 min-w-0">
                        <div className={`w-8 h-8 rounded-xl ${opt?.iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <Icon name={opt?.icon} size={15} className={opt?.iconColor} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                            <p className="text-ios-callout font-body-medium text-text-primary">{opt?.label}</p>
                            {opt?.badge && (
                              <span className={`text-ios-caption2 font-body-medium px-2 py-0.5 rounded-full ${opt?.badgeColor}`}>
                                {opt?.badge}
                              </span>
                            )}
                          </div>
                          <p className="text-ios-caption1 text-text-secondary mt-0.5 leading-relaxed">{opt?.description}</p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 mt-0.5">
                        <Toggle
                          checked={prefs?.[opt?.key] ?? true}
                          onChange={v => setDigest(opt?.key, v)}
                        />
                      </div>
                    </div>
                  ))}

                  {/* Digest schedule — only show when digest is enabled */}
                  {prefs?.digest_enabled && (
                    <div className="mt-2 pt-4 border-t border-border/30">
                      <p className="text-ios-footnote font-body-semibold text-text-primary mb-3">Planification du digest</p>
                      <div className="flex flex-wrap gap-3">
                        <div className="flex items-center space-x-2 flex-1 min-w-[160px]">
                          <Icon name="Clock" size={15} className="text-text-secondary flex-shrink-0" />
                          <div className="flex-1">
                            <label className="block text-ios-caption2 text-text-secondary mb-1">Heure d'envoi</label>
                            <select
                              value={prefs?.digest_hour ?? 8}
                              onChange={e => setPrefs(p => ({ ...p, digest_hour: parseInt(e?.target?.value) }))}
                              className="w-full px-3 py-2 border border-border rounded-xl text-ios-footnote focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold outline-none bg-white"
                            >
                              {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>{String(i)?.padStart(2, '0')}:00</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 flex-1 min-w-[160px]">
                          <Icon name="CalendarDays" size={15} className="text-text-secondary flex-shrink-0" />
                          <div className="flex-1">
                            <label className="block text-ios-caption2 text-text-secondary mb-1">Jour de la semaine</label>
                            <select
                              value={prefs?.digest_day ?? 1}
                              onChange={e => setPrefs(p => ({ ...p, digest_day: parseInt(e?.target?.value) }))}
                              className="w-full px-3 py-2 border border-border rounded-xl text-ios-footnote focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold outline-none bg-white"
                            >
                              {['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']?.map((d, i) => (
                                <option key={i} value={i}>{d}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </SectionCard>

              {/* ── Error ── */}
              {error && (
                <div className="flex items-center space-x-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-ios-callout text-red-700">
                  <Icon name="AlertCircle" size={16} className="flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* ── Save ── */}
              <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 px-5 py-4">
                <SaveButton saving={saving} saved={saved} onClick={handleSave} />
              </div>

              {/* ── Quick links ── */}
              <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 px-5 py-4">
                <p className="text-ios-footnote font-body-semibold text-text-secondary mb-3 uppercase tracking-wide">Liens rapides</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { to: '/notification-preferences', icon: 'Smartphone', iconBg: 'bg-green-50', iconColor: 'text-green-600', label: 'Préférences SMS', desc: 'Numéro et opt-in par type' },
                    { to: '/alert-preferences', icon: 'SlidersHorizontal', iconBg: 'bg-amber-50', iconColor: 'text-amber-600', label: 'Alertes propriétés', desc: 'Communes, types, prix' },
                    { to: '/notification-history', icon: 'Bell', iconBg: 'bg-brand-earth/10', iconColor: 'text-brand-earth', label: 'Historique', desc: 'Toutes vos notifications' },
                    { to: '/account-dashboard', icon: 'LayoutDashboard', iconBg: 'bg-indigo-50', iconColor: 'text-indigo-600', label: 'Mon compte', desc: 'Tableau de bord' },
                  ]?.map(link => (
                    <Link
                      key={link?.to}
                      to={link?.to}
                      className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-surface transition-colors group"
                    >
                      <div className={`w-8 h-8 rounded-lg ${link?.iconBg} flex items-center justify-center group-hover:opacity-80 transition-opacity`}>
                        <Icon name={link?.icon} size={15} className={link?.iconColor} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-ios-callout font-body-medium text-text-primary">{link?.label}</p>
                        <p className="text-ios-caption1 text-text-secondary">{link?.desc}</p>
                      </div>
                      <Icon name="ChevronRight" size={14} className="text-text-secondary flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>

            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default UserEmailSettings;
